<template>
	<form>
		<div class="d-flex flex-row nowrap justify-content-between heading">
			<span>{{ t("loginRegister.log_in_or_register") }}</span>
		</div>

		<div ref="emailwrapper" class="my-3">
			<TextInput
				:id="'email'"
				inputType="email"
				:value="emailInputVal"
				:placeholder="t('loginRegister.contacts_email')"
				:container-box-border="
					state.showErrors && (!emailInputVal || !isEmailValid)
						? '1px solid var(--primary-orange)'
						: undefined
				"
				@input="(newVal) => handleEmailChange(newVal)"
			>
				<template #after>
					<div v-if="isEmailValid && email" class="mx-3 my-2">
						<CheckSvg
							:height="16"
							:width="16"
							:color="'var(--primary-green)'"
						/>
					</div>
				</template>
			</TextInput>
			<FieldError
				class="my-1"
				ref="email-error"
				v-if="state.showErrors && (!emailInputVal || !isEmailValid)"
			>
				<template v-if="!emailInputVal">
					{{ t("loginRegister.user_info_mandatory_field") }}
				</template>
				<template v-else-if="!isEmailValid">
					{{ t("loginRegister.email_not_valid") }}
				</template>
			</FieldError>
		</div>

		<div class="">
			<Button
				:id="'check-email-button'"
				ref="check-email-button"
				:disabled="state.showErrors && (!emailInputVal || !isEmailValid)"
				@buttonClick="() => handleButtonClick()"
			>
				<template #text>
					<span>{{ t("loginRegister.login_register") }}</span>
				</template>
			</Button>
		</div>
	</form>
</template>

<script lang="ts" setup>
import { reactive, defineEmits, onMounted, onBeforeUnmount, ref } from "vue"

// Common components
import TextInput from "@/components/LoginRegister/common/inputs/Text/v1/Text.vue"
import FieldError from "@/components/LoginRegister/common/FieldError/v1/FieldError.vue"
import Button from "@/components/LoginRegister/common/Button/v1/Button.vue"
import Close from "@/components/LoginRegister/common/Close/v1/Close.vue"

// Composables
import useIsEmailValid from "@/composables/useIsEmailValid"
import useLoading, { isLoadingAny } from "@/composables/useLoading"

// libs
import scrollIntoView from "smooth-scroll-into-view-if-needed"
import { onKeyStroke } from "@vueuse/core"

// Svg
import CheckSvg from "@/components/LoginRegister/svg/CheckSvg.vue"

// Logic
import search from "@/logic/contact/search"

// state
import email from "@/state/email"

// Tracking 
import trackEmail from "@/tracking/trackEmail"

import { useI18n } from "vue-i18n"

const {t} = useI18n()

// route




interface Emits {
	(e: "emailExists", isEmailExists: boolean): void
}

// content
const content = window.content

// ref
const emailwrapper = ref()

// state
const state = reactive({
	showErrors: false,
})

// computed
const emailInputVal = ref("")
const isEmailValid = useIsEmailValid(emailInputVal)

// emit
const emit = defineEmits<Emits>()

// handlers

const handleEmailChange = (newVal: string) => {
	emailInputVal.value = newVal
}

const handleButtonClick = async () => {
	if (isLoadingAny.value) return

	if (!emailInputVal.value || !isEmailValid.value) {
		state.showErrors = true
		const emailElement = document.querySelector("#email") as HTMLInputElement
		await scrollIntoView(emailElement, {
			// scrollMode: 'if-needed',
			ease: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
			duration: 750,
			block: "center",
			inline: "nearest",
		})
		return
	}
	state.showErrors = false
	const emailValue = emailInputVal.value.toLowerCase()
	email.value = emailValue

	const { startLoadingThis, stopLoadingThis } = useLoading()
	startLoadingThis()
	const isEmailExists = await search(emailValue)
	stopLoadingThis()

	emit("emailExists", isEmailExists)
	trackEmail(emailValue)

}

onMounted(() => {
	// onKeyStroke
	const stop = onKeyStroke("Enter", () => {
		if (!emailInputVal.value) return
		handleButtonClick()
	})
	onBeforeUnmount(() => stop())

	emailwrapper.value.querySelector("input").focus()
})
</script>

<style  scoped></style>
